window.ttx = window.ttx || {};

/*
 * ==================== 插件协议（Plugin Protocol） ====================
 *
 * 全局插件注册 / 加载 API。插件脚本内通过 ttx.plugin.register() 声明自身；
 * 宿主侧通过 ttx.plugin.load() 动态加载插件脚本、ttx.plugin.install() 安装启用、
 * ttx.plugin.uninstall() 卸载。插件可访问 ttx / jQuery / vm 上下文。
 *
 * 使用示例
 * --------
 * 1) 插件脚本内注册
 *    ttx.plugin.register({
 *        name: 'my-plugin',
 *        version: '1.0.0',
 *        dependencies: ['base-plugin'],          // 必需依赖，支持数组或 { name: '^1.0.0' }
 *        optionalDependencies: ['extra'],        // 可选依赖，缺失不报错
 *        beforeInstall: function (ctx) { },      // 返回 false 可中止安装
 *        install: function (ctx) {
 *            // ctx = { name, ttx, vm, options, getJQuery: Promise<jquery> 的获取函数 }
 *            return { hello: function () { return 'hi from my-plugin'; } };
 *        },
 *        afterInstall: function (ctx, instance) { },
 *        beforeUninstall: function (ctx) { },    // 返回 false 可中止卸载
 *        uninstall: function (ctx) {
 *            // 清理逻辑
 *        },
 *        afterUninstall: function (ctx) { }
 *    });
 *
 * 2) 宿主侧加载并安装
 *    await ttx.plugin.load(['https://cdn/my-plugin.js']);  // 返回按依赖排序后的插件名
 *    ttx.plugin.installAll(['my-plugin'], { api: 'xxx' }, vm);  // 按依赖顺序批量安装
 *
 * 3) 卸载 / 体检
 *    ttx.plugin.uninstall('my-plugin');
 *    ttx.plugin.health();  // { registered, installed, missing, optionalMissing, installOrder, circular, healthy }
 * ======================================================================
 */
ttx.plugin = {};

(function () {
    // 插件注册表：name -> 插件定义
    var registry = {};
    // 已安装实例：name -> install() 返回值
    var installed = {};

    // ========== 工具函数 ==========
    function isFunction(fn) {
        return typeof fn === 'function';
    }

    function keys(obj) {
        return Object.keys(obj || {});
    }

    function difference(after, before) {
        return after.filter(function (k) {
            return before.indexOf(k) === -1;
        });
    }

    // 从全局读取插件导出（支持 window.name，兼容以全局对象方式声明的插件）
    function resolveGlobal(name) {
        try {
            return window[name];
        } catch (e) {
            return undefined;
        }
    }

    // 依赖声明归一化：支持数组 ['a','b'] 或映射 { a: '^1.0.0' }
    function normalizeDepList(value) {
        if (!value) return [];
        if (Array.isArray(value)) return value.slice();
        if (typeof value === 'object') return keys(value);
        return [];
    }

    /** 必需依赖列表 */
    function depsOf(def) {
        return normalizeDepList(def && def.dependencies);
    }

    /** 可选依赖列表（缺失不报错） */
    function optionalDepsOf(def) {
        return normalizeDepList(def && def.optionalDependencies);
    }

    /**
     * 按依赖关系拓扑排序（被依赖者在前）
     * @param {string[]} names - 待排序插件名
     * @param {boolean} includeDeps - true 时递归纳入已注册的依赖；false 时仅在 names 集合内排序
     */
    function topoSort(names, includeDeps) {
        var result = [];
        var visited = {};
        var visiting = {};
        (names || []).forEach(function visit(n) {
            if (visited[n]) return;
            if (visiting[n]) {
                throw new Error('ttx.plugin: 检测到循环依赖 -> ' + n);
            }
            visiting[n] = true;
            var def = registry[n];
            if (def) {
                depsOf(def).forEach(function (dep) {
                    var inScope = includeDeps ? !!registry[dep] : (names || []).indexOf(dep) >= 0;
                    if (inScope) visit(dep);
                });
            }
            visiting[n] = false;
            visited[n] = true;
            result.push(n);
        });
        return result;
    }

    /** 安装前校验必需依赖是否已安装，缺失则抛出明确错误 */
    function assertDependencies(name) {
        var missing = depsOf(registry[name]).filter(function (dep) {
            return installed[dep] === undefined;
        });
        if (missing.length) {
            throw new Error('ttx.plugin.install: 插件 ' + name + ' 缺少依赖 -> ' + missing.join(', '));
        }
    }

    // 归一化插件定义为统一结构
    function toPlugin(name, def) {
        if (!def || typeof def !== 'object') {
            def = {};
        }
        if (typeof def.name !== 'string') {
            def.name = name;
        }
        if (typeof def.version !== 'string') {
            def.version = '0.0.0';
        }
        return def;
    }

    // 构建注入给插件的运行时上下文
    function buildContext(name, options, vm) {
        return {
            name: name,
            ttx: ttx,
            vm: vm || null,
            options: options || {},
            // 惰性获取 jQuery（可能未加载，返回 Promise）
            getJQuery: function () {
                if (ttx.utils && typeof ttx.utils.getJQueryModule === 'function') {
                    return ttx.utils.getJQueryModule();
                }
                return Promise.resolve(typeof window.$ !== 'undefined' ? window.$ : null);
            }
        };
    }

    // 派发全局事件（依赖 ttx.events，调用时查找）
    function fireEvent(name, data) {
        if (ttx.events && typeof ttx.events.execute === 'function') {
            ttx.events.execute(name, data);
        }
    }

    // 脚本加载兜底（ttx.utils._loadScript 不可用时）
    function fallbackLoader(url) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = url;
            s.async = true;
            s.onload = resolve;
            s.onerror = function () {
                reject(new Error('插件脚本加载失败: ' + url));
            };
            document.head.appendChild(s);
        });
    }

    // ========== 对外 API ==========

    /**
     * 注册一个插件。支持 register(def) 或 register(name, def)。
     * @param {string|object} name - 插件名，或插件定义对象（必须含 name）
     * @param {object} def - 插件定义（含 install / uninstall 生命周期钩子）
     * @returns {object} 已注册的插件定义
     */
    ttx.plugin.register = function (name, def) {
        var pluginName;
        var definition;
        if (typeof name === 'string') {
            pluginName = name;
            definition = toPlugin(name, def);
        } else {
            definition = toPlugin(name.name, name);
            pluginName = definition.name;
        }
        if (!pluginName) {
            throw new Error('ttx.plugin.register: 插件缺少 name');
        }
        registry[pluginName] = definition;
        fireEvent('plugin:registered', { name: pluginName, plugin: definition });
        ttx.log && ttx.log.info('[ttx.plugin] 已注册插件: ' + pluginName);
        return definition;
    };

    /**
     * 注销插件（卸载前自动调用 uninstall 清理）。
     * @param {string} name - 插件名
     * @returns {boolean} 是否注销成功
     */
    ttx.plugin.unregister = function (name) {
        if (!registry[name]) return false;
        if (installed[name]) {
            ttx.plugin.uninstall(name);
        }
        delete registry[name];
        fireEvent('plugin:unregistered', { name: name });
        ttx.log && ttx.log.info('[ttx.plugin] 已注销插件: ' + name);
        return true;
    };

    /**
     * 判断插件是否已注册。
     * @param {string} name - 插件名
     * @returns {boolean}
     */
    ttx.plugin.has = function (name) {
        return !!registry[name];
    };

    /**
     * 获取已注册的插件定义。
     * @param {string} name - 插件名
     * @returns {object|undefined}
     */
    ttx.plugin.get = function (name) {
        return registry[name];
    };

    /**
     * 列出全部已注册插件名。
     * @returns {string[]}
     */
    ttx.plugin.list = function () {
        return keys(registry);
    };

    /**
     * 动态加载一个或多个插件脚本（支持插件脚本自行 register / 以全局对象暴露）。
     * @param {string|string[]} urls - 脚本地址或地址数组
     * @param {string|object} [opts] - 插件名（load(urls, name)），或 { name } 配置对象
     * @param {Function} [callback] - (err, detectedNames) 回调
     * @returns {Promise<string[]>} 新加载的插件名列表
     * @example
     * await ttx.plugin.load(['/static/plugins/my-plugin.js']);
     * await ttx.plugin.load('/static/plugins/foo.js', 'fooPlugin');
     */
    ttx.plugin.load = function (urls, opts, callback) {
        var name = null;
        if (typeof opts === 'string') {
            name = opts;
        } else if (opts && typeof opts === 'object') {
            name = opts.name;
        } else if (typeof opts === 'function') {
            callback = opts;
        }
        if (typeof callback !== 'function') callback = null;

        var list = typeof urls === 'string' ? [urls] : (Array.isArray(urls) ? urls.slice() : []);
        if (!list.length) {
            var empty = [];
            if (callback) callback(null, empty);
            return Promise.resolve(empty);
        }

        var loader = (ttx.utils && typeof ttx.utils._loadScript === 'function')
            ? ttx.utils._loadScript
            : fallbackLoader;

        // 加载前的注册表快照，用于识别"本次新加载的插件"
        var before = keys(registry);

        var chain = list.reduce(function (prev, url) {
            return prev.then(function () {
                ttx.log && ttx.log.info('[ttx.plugin] 加载插件脚本: ' + url);
                return loader(url);
            });
        }, Promise.resolve());

        return chain.then(function () {
            // 脚本执行后若指定了 name 且未自行注册，尝试从全局对象读取
            if (name && !registry[name]) {
                var globalDef = resolveGlobal(name);
                if (globalDef !== undefined) {
                    registry[name] = toPlugin(name, globalDef);
                }
            }
            var after = keys(registry);
            var detected = difference(after, before);
            // 按依赖关系排序（被依赖者在前），便于调用方直接按序 install
            var ordered = topoSort(detected, false);
            fireEvent('plugin:loaded', { names: ordered });
            ttx.log && ttx.log.info('[ttx.plugin] 脚本加载完成，识别到插件: ' + (ordered.join(',') || '无'));
            if (callback) callback(null, ordered);
            return ordered;
        }).catch(function (err) {
            ttx.console && ttx.console.error('[ttx.plugin.load] 插件脚本加载失败:', err);
            if (callback) callback(err);
            throw err;
        });
    };

    /**
     * 安装（启用）一个插件。
     * 生命周期：beforeInstall(ctx) -> install(ctx) -> afterInstall(ctx, instance)
     * beforeInstall 返回 false 可中止安装。
     * @param {string} name - 插件名
     * @param {object} [options] - 传给插件的配置
     * @param {object} [vm] - 可选的 ViewModel 上下文
     * @param {boolean} [force=false] - 是否强制重新安装
     * @returns {*} 插件 install() 的返回值；已安装且非 force 时返回既有实例
     */
    ttx.plugin.install = function (name, options, vm, force) {
        var def = registry[name];
        if (!def) {
            var err = new Error('ttx.plugin.install: 插件未注册 -> ' + name);
            ttx.console && ttx.console.error('[ttx.plugin]', err);
            throw err;
        }
        if (!force && installed[name] !== undefined) {
            return installed[name];
        }
        assertDependencies(name);
        var ctx = buildContext(name, options, vm);
        if (isFunction(def.beforeInstall) && def.beforeInstall(ctx) === false) {
            ttx.log && ttx.log.info('[ttx.plugin] beforeInstall 中止安装: ' + name);
            return undefined;
        }
        var instance;
        if (isFunction(def.install)) {
            instance = def.install(ctx);
        }
        installed[name] = instance;
        if (isFunction(def.afterInstall)) {
            def.afterInstall(ctx, instance);
        }
        fireEvent('plugin:installed', { name: name, instance: instance, options: options });
        ttx.log && ttx.log.info('[ttx.plugin] 已安装插件: ' + name);
        return instance;
    };

    /**
     * 批量安装：先按依赖拓扑排序（被依赖者先装），再依次安装。
     * @param {string|string[]} names - 插件名或名称数组
     * @param {object} [options] - 传给插件的配置
     * @param {object} [vm] - 可选的 ViewModel 上下文
     * @returns {Array} 各插件 install() 的返回值（与安装顺序一致）
     */
    ttx.plugin.installAll = function (names, options, vm) {
        var list = typeof names === 'string' ? [names] : (Array.isArray(names) ? names : []);
        return topoSort(list, true).map(function (n) {
            return ttx.plugin.install(n, options, vm);
        });
    };

    /**
     * 卸载（禁用）一个插件。
     * 生命周期：beforeUninstall(ctx) -> uninstall(ctx) -> afterUninstall(ctx)
     * beforeUninstall 返回 false 可中止卸载。
     * @param {string} name - 插件名
     * @param {object} [options] - 传给 uninstall 的配置
     * @returns {boolean} 是否成功卸载
     */
    ttx.plugin.uninstall = function (name, options) {
        var def = registry[name];
        if (installed[name] === undefined) return false;
        var ctx = buildContext(name, options, null);
        if (def && isFunction(def.beforeUninstall) && def.beforeUninstall(ctx) === false) {
            ttx.log && ttx.log.info('[ttx.plugin] beforeUninstall 中止卸载: ' + name);
            return false;
        }
        if (def && isFunction(def.uninstall)) {
            def.uninstall(ctx);
        }
        delete installed[name];
        if (def && isFunction(def.afterUninstall)) {
            def.afterUninstall(ctx);
        }
        fireEvent('plugin:uninstalled', { name: name });
        ttx.log && ttx.log.info('[ttx.plugin] 已卸载插件: ' + name);
        return true;
    };

    /**
     * 健康检查：已注册 / 已安装 / 依赖异常 / 安装顺序。
     * @returns {{registered: string[], installed: string[], missing: Array, optionalMissing: Array, installOrder: string[], circular: string[], healthy: boolean}}
     */
    ttx.plugin.health = function () {
        var missing = [];
        var optionalMissing = [];
        keys(registry).forEach(function (n) {
            var def = registry[n];
            depsOf(def).forEach(function (dep) {
                if (!registry[dep]) missing.push({ name: n, dependency: dep, optional: false });
            });
            optionalDepsOf(def).forEach(function (dep) {
                if (!registry[dep]) optionalMissing.push({ name: n, dependency: dep, optional: true });
            });
        });

        var installOrder = [];
        var circular = [];
        try {
            installOrder = topoSort(keys(registry), true);
        } catch (e) {
            circular.push(e.message);
        }

        return {
            registered: keys(registry),
            installed: keys(installed),
            missing: missing,
            optionalMissing: optionalMissing,
            installOrder: installOrder,
            circular: circular,
            healthy: missing.length === 0 && circular.length === 0
        };
    };

    // 同义词：use/activate -> install，deactivate -> uninstall
    ttx.plugin.use = ttx.plugin.install;
    ttx.plugin.activate = ttx.plugin.install;
    ttx.plugin.deactivate = ttx.plugin.uninstall;
})();